"use client";

import { ArrowRight } from "@phosphor-icons/react";
import Image from "next/image";
import { useTranslations } from "next-intl";

import { Link } from "@/navigation";

interface CaseCard {
	keyPrefix: string;
	image: string;
}

interface IndustryCaseStudiesProps {
	namespace: string;
	cases: readonly CaseCard[];
	brandColor?: string;
}

export default function IndustryCaseStudies({
	namespace,
	cases,
	brandColor = "#64a525",
}: IndustryCaseStudiesProps) {
	const t = useTranslations(namespace);

	return (
		<section className="bg-[#fcfff4] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<header className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("caseStudiesTitle")}
					</h2>
					<p className="mx-auto text-sm leading-relaxed text-black md:text-base">{t("caseStudiesDesc")}</p>
				</header>

				<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
					{cases.map((card) => (
						<article
							key={card.keyPrefix}
							className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-shadow duration-300 hover:shadow-md"
						>
							<div className="relative h-56 w-full sm:h-64 md:h-60 lg:h-72">
								<Image
									src={card.image}
									alt={t(`${card.keyPrefix}Alt`)}
									fill
									className="object-cover"
									sizes="(max-width: 768px) 100vw, 50vw"
								/>
							</div>
							<div className="p-6 sm:p-7 md:px-5 md:py-8">
								<div className="mb-4 flex flex-wrap items-center gap-2">
									<span
										className="rounded-full bg-[#ebf5ea] px-4 py-1 text-xs font-semibold sm:text-sm"
										style={{ color: brandColor }}
									>
										{t(`${card.keyPrefix}Tag`)}
									</span>
									<span className="text-sm text-gray-500 sm:text-base">| {t(`${card.keyPrefix}Meta`)}</span>
								</div>
								<h3 className="mb-3 text-lg font-bold leading-snug text-black sm:text-xl md:text-2xl">
									{t(`${card.keyPrefix}Title`)}
								</h3>
								<p className="mb-5 text-sm leading-relaxed text-black md:text-base">
									{t(`${card.keyPrefix}Desc`)}
								</p>
								<div className="mb-6 flex justify-around pt-4">
									{[1, 2, 3].map((i) => (
										<div key={i}>
											<p className="text-xs text-gray-500 sm:text-sm">
												{t(`${card.keyPrefix}Spec${i}Label`)}
											</p>
											<p className="mt-0.5 text-sm text-black sm:text-base">
												{t(`${card.keyPrefix}Spec${i}Value`)}
											</p>
										</div>
									))}
								</div>
								<div className="flex flex-wrap items-center justify-between gap-3">
									<Link
										href="/cases"
										className="inline-flex items-center gap-1.5 text-sm font-semibold transition-opacity duration-200 hover:opacity-75 sm:text-base"
										style={{ color: brandColor }}
									>
										{t("caseStudiesCtaLink")}
										<ArrowRight size={20} weight="bold" aria-hidden="true" />
									</Link>
									<Link
										href="/upload"
										className="inline-flex items-center justify-center rounded px-4 py-2 text-sm font-semibold text-white transition-opacity hover:text-white hover:opacity-90"
										style={{ backgroundColor: brandColor }}
									>
										{t("caseStudiesQuoteButton")}
									</Link>
								</div>
							</div>
						</article>
					))}
				</div>
			</div>
		</section>
	);
}
